import React from 'react';
// mui components
import { Avatar, Box, Button, Stack, Typography } from '@mui/material';
// hooks
import { IArtistProfile } from 'hooks/swrRequests/useArtistProfiles';
// config
import { getProfileDisplayName, getProfileProgress } from '../steps';
import { useTranslation } from 'react-i18next';

const GREEN = '#1F9D6B';

interface OwnProps {
  profiles: IArtistProfile[];
  currentId: number;
  onSelect: (id: number) => void;
}

const ArtistBar: React.FC<OwnProps> = ({ profiles, currentId, onSelect }) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontSize: 16, color: '#8C8C8C', mb: 1.2 }}>
        {t('You manage {{count}} artists — each needs its own profile.', {
          count: profiles.length,
        })}
      </Typography>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
        {profiles.map((profile) => {
          const { done, total } = getProfileProgress(profile);
          const active = profile.id === currentId;
          const name = getProfileDisplayName(profile) || t('New artist');
          return (
            <Button
              key={profile.id}
              disableRipple
              onClick={() => onSelect(profile.id)}
              sx={{
                textTransform: 'none',
                borderRadius: 2.5,
                px: 1.7,
                py: 1,
                border: active ? '1.5px solid #141414' : '1px solid #E9E9E9',
                bgcolor: active ? '#FAFAFA' : 'transparent',
                color: '#141414',
                '&:hover': { bgcolor: '#FAFAFA', borderColor: active ? '#141414' : '#CFCFCF' },
              }}
            >
              <Stack direction="row" spacing={1.2} alignItems="center">
                <Avatar
                  src={profile.artistImage || undefined}
                  alt={name}
                  sx={{
                    width: 28,
                    height: 28,
                    fontSize: 12,
                    fontWeight: 700,
                    bgcolor: profile.completed ? GREEN : '#141414',
                    color: '#fff',
                  }}
                >
                  {profile.completed ? '✓' : name.slice(0, 1).toUpperCase()}
                </Avatar>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography
                    sx={{ fontSize: 16, fontWeight: active ? 700 : 600, lineHeight: 1.2 }}
                  >
                    {name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 16,
                      color: profile.completed ? GREEN : '#8C8C8C',
                      lineHeight: 1.3,
                    }}
                  >
                    {profile.completed ? t('Complete') : `${done} / ${total} ${t('fields')}`}
                  </Typography>
                </Box>
              </Stack>
            </Button>
          );
        })}
      </Stack>
    </Box>
  );
};

export default ArtistBar;
